/**
 * Platform admin -- AI Readiness Snapshot intake delivery flow.
 *
 * Inbound side: marketing /snapshot form -> POST /api/public/snapshot-intake ->
 * MarketingLead row with resource='snapshot-intake' + intake JSON in source.
 *
 * Delivery side (this page): Dom sees incoming intakes, generates a templated
 * 1-pager draft, edits the markdown, sends via email button. The endpoint also
 * audits the send.
 *
 * No PDF rendering yet -- the email body IS the deliverable. A future iteration
 * can wrap the same markdown in the existing PDF renderer.
 */

import { useState } from 'react';
import { useQuery, useMutation } from '@tanstack/react-query';
import {
  Inbox,
  Send,
  FileText,
  Sparkles,
  Mail,
  Globe,
  Layers,
  AlertTriangle,
  CalendarClock,
  Download,
} from 'lucide-react';
import { api, extractApiError } from '@/lib/api';
import { toast } from '@/stores/toast';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Skeleton } from '@/components/ui/skeleton';
import { cn, formatDate } from '@/lib/utils';

interface IntakeListItem {
  id: string;
  email: string;
  name: string | null;
  company: string | null;
  createdAt: string;
  ipAddress: string | null;
  systemCount: number;
  frameworkCount: number;
  riskCount: number;
  procurementWindow: string | null;
  country: string | null;
}

interface IntakeFull {
  kind: string;
  capturedAt: string;
  role: string | null;
  country: string | null;
  aiSystems: Array<{ name: string; vendor?: string; useCase?: string }>;
  topRisks: string[];
  frameworks: string[];
  procurementWindow: string | null;
  notes: string | null;
}

interface IntakeDetail {
  id: string;
  email: string;
  name: string | null;
  company: string | null;
  createdAt: string;
  ipAddress: string | null;
  userAgent: string | null;
  intake: IntakeFull | null;
}

const FRAMEWORK_LABELS: Record<string, string> = {
  'eu-ai-act': 'EU AI Act',
  'annex-22': 'Annex 22',
  'cfr-part-11': '21 CFR Part 11',
  'gdpr-article-30': 'GDPR Art. 30',
  'iso-42001': 'ISO 42001',
};

export function PlatformSnapshotIntakesPage() {
  const list = useQuery({
    queryKey: ['platform-admin', 'snapshot-intakes'],
    queryFn: async () => (await api.get<{ items: IntakeListItem[] }>('/api/platform-admin/snapshot-intakes')).data,
    refetchInterval: 60_000,
  });

  const [activeId, setActiveId] = useState<string | null>(null);

  if (list.isLoading) {
    return <Skeleton className="h-32" />;
  }

  const items = list.data?.items ?? [];

  return (
    <div className="space-y-6">
      <div>
        <h1 className="font-display text-2xl">Snapshot intakes</h1>
        <p className="mt-1 max-w-2xl text-sm text-muted-foreground">
          Inbound AI Readiness Snapshot requests from <code>praxara.io/snapshot</code>. SLA: 3 business days
          from intake. GBP 2,500 invoiced on delivery.
        </p>
      </div>

      <div className="grid gap-3 md:grid-cols-3">
        <Stat label="Total intakes" value={items.length} icon={Inbox} />
        <Stat
          label="Last 7 days"
          value={items.filter((i) => Date.now() - new Date(i.createdAt).getTime() < 7 * 86_400_000).length}
          icon={CalendarClock}
        />
        <Stat
          label="Average AI systems"
          value={items.length === 0 ? '0' : Math.round(items.reduce((acc, i) => acc + i.systemCount, 0) / items.length)}
          icon={Layers}
        />
      </div>

      {items.length === 0 ? (
        <Card>
          <CardContent className="p-10 text-center text-sm text-muted-foreground">
            <Inbox className="mx-auto h-8 w-8 opacity-60" />
            <p className="mt-3">No snapshot intakes yet. Form is at <a className="underline" href="https://praxara.io/snapshot" target="_blank" rel="noreferrer">praxara.io/snapshot</a>.</p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-4 md:grid-cols-[1fr_2fr]">
          {/* List */}
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-base">Inbox</CardTitle>
              <CardDescription>{items.length} intake{items.length === 1 ? '' : 's'}. Click any to draft a deliverable.</CardDescription>
            </CardHeader>
            <CardContent className="p-0">
              <div className="divide-y">
                {items.map((it) => {
                  const isActive = activeId === it.id;
                  const ageDays = Math.floor((Date.now() - new Date(it.createdAt).getTime()) / 86_400_000);
                  return (
                    <button
                      key={it.id}
                      type="button"
                      onClick={() => setActiveId(it.id)}
                      className={cn(
                        'block w-full p-3 text-left text-sm transition-colors hover:bg-muted/40',
                        isActive && 'bg-primary/5 ring-1 ring-inset ring-primary/40',
                      )}
                    >
                      <div className="flex items-center justify-between gap-2">
                        <span className="truncate font-medium">{it.company ?? it.email}</span>
                        <Badge variant={ageDays >= 3 ? 'destructive' : ageDays >= 1 ? 'warning' : 'secondary'}>
                          {ageDays === 0 ? 'today' : `${ageDays}d`}
                        </Badge>
                      </div>
                      <div className="mt-1 truncate text-xs text-muted-foreground">{it.name} · {it.email}</div>
                      <div className="mt-1 flex flex-wrap gap-1 text-[10px]">
                        <span className="rounded bg-muted px-1.5 py-0.5">{it.systemCount} systems</span>
                        <span className="rounded bg-muted px-1.5 py-0.5">{it.frameworkCount} frameworks</span>
                        {it.riskCount > 0 && <span className="rounded bg-muted px-1.5 py-0.5">{it.riskCount} risks</span>}
                        {it.country && <span className="rounded bg-muted px-1.5 py-0.5">{it.country}</span>}
                      </div>
                    </button>
                  );
                })}
              </div>
            </CardContent>
          </Card>

          {/* Detail / draft */}
          <div>
            {activeId ? <IntakeDetail id={activeId} /> : (
              <Card>
                <CardContent className="p-10 text-center text-sm text-muted-foreground">
                  <Sparkles className="mx-auto h-8 w-8 opacity-60" />
                  <p className="mt-3">Pick an intake from the inbox to start drafting.</p>
                </CardContent>
              </Card>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function Stat({ label, value, icon: Icon }: { label: string; value: string | number; icon: React.ComponentType<{ className?: string }> }) {
  return (
    <Card>
      <CardContent className="flex items-center gap-3 p-5">
        <div className="flex h-9 w-9 items-center justify-center rounded-md bg-primary/10 text-primary">
          <Icon className="h-4 w-4" />
        </div>
        <div>
          <div className="font-display text-2xl">{value}</div>
          <div className="text-xs text-muted-foreground">{label}</div>
        </div>
      </CardContent>
    </Card>
  );
}

function IntakeDetail({ id }: { id: string }) {
  const detail = useQuery({
    queryKey: ['platform-admin', 'snapshot-intakes', id],
    queryFn: async () => (await api.get<IntakeDetail>(`/api/platform-admin/snapshot-intakes/${id}`)).data,
  });

  const [draft, setDraft] = useState<string>('');
  const [draftLoaded, setDraftLoaded] = useState(false);

  const generateDraft = useMutation({
    mutationFn: async () =>
      (await api.post<{ draft: string }>(`/api/platform-admin/snapshot-intakes/${id}/draft-report`)).data,
    onSuccess: (d) => {
      setDraft(d.draft);
      setDraftLoaded(true);
      toast.success('Draft generated', 'Edit the markdown below; the customer sees the rendered HTML.');
    },
    onError: (err) => toast.error('Draft failed', extractApiError(err).message),
  });

  const generateAiDraft = useMutation({
    mutationFn: async () =>
      (await api.post<{ draft: string; metadata?: { overallRating?: string; redCount?: number; amberCount?: number; greenCount?: number } }>(`/api/platform-admin/snapshot-intakes/${id}/ai-draft`)).data,
    onSuccess: (d) => {
      setDraft(d.draft);
      setDraftLoaded(true);
      const m = d.metadata;
      const summary = m ? `${m.overallRating} — ${m.redCount} RED / ${m.amberCount} AMBER / ${m.greenCount} GREEN` : '';
      toast.success('AI draft generated', summary || 'Review the assessment below before sending.');
    },
    onError: (err) => toast.error('AI draft failed', extractApiError(err).message),
  });

  const [to, setTo] = useState('');
  const [subject, setSubject] = useState('');

  const send = useMutation({
    mutationFn: async () =>
      (await api.post<{ ok: boolean; sentAt: string }>(`/api/platform-admin/snapshot-intakes/${id}/send`, {
        to,
        subject,
        bodyMarkdown: draft,
      })).data,
    onSuccess: () => {
      toast.success('Sent', `Deliverable emailed to ${to}.`);
    },
    onError: (err) => toast.error('Send failed', extractApiError(err).message),
  });

  if (detail.isLoading || !detail.data) return <Skeleton className="h-64" />;

  const d = detail.data;
  const intake = d.intake;

  if (!to && d.email) setTo(d.email);
  if (!subject && d.company) setSubject(`AI Readiness Snapshot for ${d.company} -- Praxara`);

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base">{d.company ?? d.email}</CardTitle>
          <CardDescription>
            {d.name} · {d.email} · received {formatDate(d.createdAt)}
            {d.ipAddress && ` · from ${d.ipAddress}`}
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-3 text-sm">
          {intake ? (
            <>
              <div className="grid gap-2 text-xs sm:grid-cols-2">
                <Field icon={Globe} label="Country" value={intake.country ?? '-'} />
                <Field icon={Mail} label="Role" value={intake.role ?? '-'} />
                <Field icon={CalendarClock} label="Procurement window" value={intake.procurementWindow ?? '-'} />
                <Field icon={Layers} label="AI systems" value={String(intake.aiSystems.length)} />
              </div>

              <div>
                <div className="mt-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground">AI inventory</div>
                <ul className="mt-1 space-y-1 text-xs">
                  {intake.aiSystems.map((s, i) => (
                    <li key={i} className="rounded-md border bg-muted/30 px-3 py-2">
                      <strong>{s.name}</strong>{s.vendor ? ` -- ${s.vendor}` : ''}
                      {s.useCase && <div className="mt-0.5 text-muted-foreground">{s.useCase}</div>}
                    </li>
                  ))}
                </ul>
              </div>

              <div>
                <div className="mt-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground">Frameworks requested</div>
                <div className="mt-1 flex flex-wrap gap-1">
                  {intake.frameworks.map((f) => <Badge key={f} variant="info">{FRAMEWORK_LABELS[f] ?? f}</Badge>)}
                </div>
              </div>

              {intake.topRisks.length > 0 && (
                <div>
                  <div className="mt-2 flex items-center gap-1 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                    <AlertTriangle className="h-3 w-3" /> Top risks
                  </div>
                  <ol className="mt-1 list-decimal pl-5 text-xs">
                    {intake.topRisks.map((r, i) => <li key={i}>{r}</li>)}
                  </ol>
                </div>
              )}

              {intake.notes && (
                <div>
                  <div className="mt-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground">Customer notes</div>
                  <div className="mt-1 rounded-md border bg-muted/30 p-2 text-xs italic">{intake.notes}</div>
                </div>
              )}
            </>
          ) : (
            <p className="text-xs text-muted-foreground">Intake source could not be parsed.</p>
          )}
        </CardContent>
      </Card>

      {/* Draft + send */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2">
            <FileText className="h-4 w-4 text-primary" /> Draft deliverable
          </CardTitle>
          <CardDescription>
            Generate the templated 1-pager, edit the markdown, then send. The customer sees the rendered HTML.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          {!draftLoaded && (
            <div className="flex gap-2">
              <Button onClick={() => generateAiDraft.mutate()} disabled={generateAiDraft.isPending}>
                <Sparkles className="mr-1.5 h-3.5 w-3.5" /> {generateAiDraft.isPending ? 'AI generating (30-60s)...' : 'Generate AI draft'}
              </Button>
              <Button variant="outline" onClick={() => generateDraft.mutate()} disabled={generateDraft.isPending}>
                {generateDraft.isPending ? 'Generating...' : 'Template only'}
              </Button>
            </div>
          )}
          {draftLoaded && (
            <>
              <div className="grid gap-2 sm:grid-cols-2">
                <div>
                  <Label>To (email)</Label>
                  <Input value={to} onChange={(e) => setTo(e.target.value)} type="email" />
                </div>
                <div>
                  <Label>Subject</Label>
                  <Input value={subject} onChange={(e) => setSubject(e.target.value)} />
                </div>
              </div>
              <div>
                <Label>Body (markdown)</Label>
                <Textarea
                  rows={20}
                  value={draft}
                  onChange={(e) => setDraft(e.target.value)}
                  className="font-mono text-xs"
                />
              </div>
              <div className="flex flex-wrap items-center gap-2">
                <Button onClick={() => send.mutate()} disabled={!to || !subject || draft.length < 50 || send.isPending}>
                  <Send className="mr-1.5 h-3.5 w-3.5" /> {send.isPending ? 'Sending...' : 'Send to customer'}
                </Button>
                <Button
                  variant="outline"
                  type="button"
                  disabled={draft.length < 50}
                  onClick={async () => {
                    try {
                      const res = await api.post(`/api/platform-admin/snapshot-intakes/${id}/pdf`, {
                        bodyMarkdown: draft,
                        title: subject,
                      }, { responseType: 'blob' });
                      const blob = new Blob([res.data as Blob], { type: 'application/pdf' });
                      const url = URL.createObjectURL(blob);
                      const a = document.createElement('a');
                      a.href = url;
                      a.download = `praxara-snapshot-${(d.company ?? 'customer').replace(/[^a-z0-9]+/gi, '-').toLowerCase()}.pdf`;
                      document.body.appendChild(a);
                      a.click();
                      a.remove();
                      URL.revokeObjectURL(url);
                      toast.success('PDF downloaded', 'Attached to a new tab + saved to disk.');
                    } catch (err) {
                      toast.error('PDF render failed', extractApiError(err).message);
                    }
                  }}
                >
                  <Download className="mr-1.5 h-3.5 w-3.5" /> Download PDF
                </Button>
                <Button variant="outline" onClick={() => generateAiDraft.mutate()} disabled={generateAiDraft.isPending}>
                  <Sparkles className="mr-1.5 h-3.5 w-3.5" /> {generateAiDraft.isPending ? 'AI generating...' : 'AI draft (full assessment)'}
                </Button>
                <Button variant="ghost" size="sm" onClick={() => generateDraft.mutate()}>
                  Regenerate template
                </Button>
                <span className="text-xs text-muted-foreground">{draft.length.toLocaleString()} chars · email + PDF both audit-logged.</span>
              </div>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

function Field({ icon: Icon, label, value }: { icon: React.ComponentType<{ className?: string }>; label: string; value: string }) {
  return (
    <div className="flex items-start gap-2 rounded-md border bg-muted/30 px-3 py-2">
      <Icon className="mt-0.5 h-3 w-3 shrink-0 text-muted-foreground" />
      <div className="min-w-0">
        <div className="text-[10px] uppercase tracking-wider text-muted-foreground">{label}</div>
        <div className="truncate font-medium">{value}</div>
      </div>
    </div>
  );
}
